// ABOUTME: Global application state context for Obsidian BI.
// ABOUTME: Manages app mode, investigation state, workspace, and sessions.

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import {
  AppMode,
  InvestigationState,
  type Insight,
  type InvestigationResult,
  type ChatMessage,
  type InvestigationSession,
} from '@/types';
import { investigationApi, userApi, featuresApi, setWorkspaceIdGetter } from '@/services/api/client';
import type { WorkspaceMembership, FeatureFlags } from '@/types/api';
import { useAuth } from '@/context/AuthContext';

/**
 * Derive self-service and free-tier flags from workspace memberships.
 * Self-service tiers: 'freemium' and 'base_individual'.
 * Free tier: 'freemium' only.
 */
export function deriveTierFlags(
  workspaces: WorkspaceMembership[],
  currentWorkspaceId: string
): { isSelfService: boolean; isFreeTier: boolean } {
  const tier = workspaces.find(
    (w) => w.workspace_id === currentWorkspaceId
  )?.subscription_tier ?? null;
  const isSelfService = tier === 'freemium' || tier === 'base_individual';
  const isFreeTier = tier === 'freemium';
  return { isSelfService, isFreeTier };
}

interface AppContextType {
  // App Mode
  mode: AppMode;
  setMode: (mode: AppMode) => void;

  // Investigation State
  investigationState: InvestigationState;
  setInvestigationState: (state: InvestigationState) => void;

  // Investigation Sessions
  query: string;
  setQuery: (q: string) => void;
  currentSessionId: string | null;
  sessions: InvestigationSession[];
  startNewSession: (isTourDemo?: boolean, dataSourceIds?: string[]) => Promise<string>;
  loadSession: (sessionId: string) => void;
  deleteSession: (sessionId: string) => Promise<void>;
  refreshSessions: () => Promise<void>;
  isLoadingSessions: boolean;

  // Data State
  insights: Insight[];
  setInsights: (insights: Insight[]) => void;
  currentInvestigation: InvestigationResult | null;
  setCurrentInvestigation: (result: InvestigationResult | null) => void;
  chatHistory: ChatMessage[];
  setChatHistory: (messages: ChatMessage[]) => void;
  addChatMessage: (message: ChatMessage) => void;

  // Loading State
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;

  // Error State
  error: string | null;
  setError: (error: string | null) => void;
  clearError: () => void;

  // Workspace Context
  currentWorkspaceId: string;
  setCurrentWorkspaceId: (workspaceId: string) => void;
  availableWorkspaces: WorkspaceMembership[];
  switchWorkspace: (workspaceId: string) => Promise<void>;
  isLoadingWorkspaces: boolean;

  // User Profile (from backend, not Auth0)
  userProfileId: string | null;
  userProfileName: string | null;

  // Subscription tier flags (derived from current workspace)
  isSelfService: boolean;
  isFreeTier: boolean;
  tourCompleted: boolean;

  // Demo data source state (derived from workspace membership)
  hasDemoDataSource: boolean;
  demoDataSourceName: string | null;
  demoDismissed: boolean;
  dismissDemo: () => Promise<void>;

  // Feature flags (from backend)
  featureFlags: FeatureFlags;
  isFeatureFlagsLoaded: boolean;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

const WORKSPACE_STORAGE_KEY = 'obsidian-bi:selected-workspace';

function getStoredWorkspaceId(): string | null {
  try {
    return localStorage.getItem(WORKSPACE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function setStoredWorkspaceId(workspaceId: string): void {
  try {
    localStorage.setItem(WORKSPACE_STORAGE_KEY, workspaceId);
  } catch (err) {
    console.error('Failed to store workspace ID:', err);
  }
}

function clearStoredWorkspaceId(): void {
  try {
    localStorage.removeItem(WORKSPACE_STORAGE_KEY);
  } catch (err) {
    console.error('Failed to clear workspace ID:', err);
  }
}

const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  public_report_viewer: false,
  report_comments: false,
  demo_datasets: false,
  updated_freemium_limits: false,
  watermarked_exports: false,
  domain_clustering_admin: false,
  invite_prompts: false,
  shadow_it_detection: false,
  investigate_from_report: false,
  public_gallery: false,
  insight_sharing: false,
};

export function AppProvider({ children }: AppProviderProps) {
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth();

  // App Mode State
  const [mode, setMode] = useState<AppMode>(AppMode.Home);
  const [investigationState, setInvestigationState] = useState<InvestigationState>(
    InvestigationState.Idle
  );

  // Investigation State
  const [query, setQuery] = useState('');
  const [sessions, setSessions] = useState<InvestigationSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [currentInvestigation, setCurrentInvestigation] = useState<InvestigationResult | null>(null);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);

  // Data State
  const [insights, setInsights] = useState<Insight[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Workspace State - initialize from localStorage first to avoid race condition
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string>(() => {
    // Try localStorage first (persisted workspace selection)
    const stored = getStoredWorkspaceId();
    if (stored) return stored;

    // Fall back to user's workspace (may be undefined during initial load)
    if (user?.workspace_id) return user.workspace_id;

    // Empty string as last resort - will be set by fetchWorkspaces
    return '';
  });
  const [availableWorkspaces, setAvailableWorkspaces] = useState<WorkspaceMembership[]>([]);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(false);
  const [userProfileId, setUserProfileId] = useState<string | null>(null);
  const [userProfileName, setUserProfileName] = useState<string | null>(null);
  const [tourCompleted, setTourCompleted] = useState(false);

  // Plan features state (effective features from usage endpoint)

  // Feature flags state
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(DEFAULT_FEATURE_FLAGS);
  const [isFeatureFlagsLoaded, setIsFeatureFlagsLoaded] = useState(false);

  // Fetch feature flags on mount (public endpoint, no auth needed)
  useEffect(() => {
    const fetchFlags = async () => {
      try {
        const flags = await featuresApi.getFlags();
        setFeatureFlags(flags);
      } catch (err) {
        console.error('Failed to fetch feature flags:', err);
        // Keep defaults on error
      } finally {
        setIsFeatureFlagsLoaded(true);
      }
    };
    fetchFlags();
  }, []);

  // Sync workspace ID when user loads (handles initial auth)
  useEffect(() => {
    if (user?.workspace_id && !currentWorkspaceId) {
      setCurrentWorkspaceId(user.workspace_id);
      setStoredWorkspaceId(user.workspace_id);
    }
  }, [user?.workspace_id, currentWorkspaceId]);

  // Set workspace ID getter for API client - this ensures all API calls use the selected workspace
  useEffect(() => {
    if (currentWorkspaceId) {
      setWorkspaceIdGetter(() => currentWorkspaceId);
    }
  }, [currentWorkspaceId]);

  // Fetch investigations from API on mount
  const refreshSessions = useCallback(async () => {
    setIsLoadingSessions(true);
    try {
      const response = await investigationApi.list({ limit: 50 });
      // Map backend response to our session format
      // Backend returns metadata.total_messages, we need to extract it
      const apiSessions: InvestigationSession[] = response.investigations.map((inv: any) => {
        // Get the timestamp string - prefer last_activity_at, fall back to updated_at
        const lastActivityStr: string = inv.metadata?.last_activity_at || inv.updated_at;
        return {
          id: inv.id,
          title: inv.title,
          lastActive: new Date(lastActivityStr + 'Z'), // Append Z to parse as UTC
          lastActiveRaw: lastActivityStr, // Keep raw string for display
          messages: [], // Messages are loaded on demand
          messageCount: inv.metadata?.total_messages || 0,
          dataSourceIds: inv.data_source_ids || [],
          lastEditedAt: inv.last_edited_at ? new Date(inv.last_edited_at) : null,
          lastEditedBy: inv.last_edited_by || null,
        };
      });
      setSessions(apiSessions);
    } catch (err) {
      console.error('Failed to fetch investigations:', err);
      // Keep existing sessions on error
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  // Load investigations when authenticated
  useEffect(() => {
    if (isAuthenticated && !isAuthLoading) {
      refreshSessions();
    }
  }, [isAuthenticated, isAuthLoading, refreshSessions]);

  // Sync ChatHistory to Sessions
  useEffect(() => {
    if (currentSessionId && chatHistory.length > 0) {
      setSessions((prev) =>
        prev.map((s) => {
          if (s.id === currentSessionId) {
            return { ...s, messages: chatHistory };
          }
          return s;
        })
      );
    }
  }, [chatHistory, currentSessionId]);

  // Fetch user's workspace memberships
  const fetchWorkspaces = useCallback(async () => {
    if (!isAuthenticated || isAuthLoading) {
      return;
    }

    setIsLoadingWorkspaces(true);
    try {
      const profile = await userApi.me();
      setAvailableWorkspaces(profile.workspaces);
      setUserProfileId(profile.id);
      setUserProfileName(profile.name);
      setTourCompleted(profile.preferences?.tour_completed ?? false);

      // Validate stored workspace ID
      const storedId = getStoredWorkspaceId();
      if (storedId) {
        const hasAccess = profile.workspaces.some(
          (w) => w.workspace_id === storedId
        );
        if (hasAccess) {
          setCurrentWorkspaceId(storedId);
        } else {
          // Stored workspace no longer accessible
          clearStoredWorkspaceId();
          setCurrentWorkspaceId(profile.primary_workspace_id);
        }
      } else {
        setCurrentWorkspaceId(profile.primary_workspace_id);
      }
    } catch (err) {
      console.error('Failed to fetch workspaces:', err);
      // Fallback to single-workspace mode
      if (user?.workspace_id) {
        setAvailableWorkspaces([
          {
            workspace_id: user.workspace_id,
            workspace_name: 'Default Workspace',
            role: user.role as 'workspace-admin' | 'user',
            subscription_tier: 'freemium',
            has_demo_data_source: false,
            demo_data_source_name: null,
            demo_dismissed: false,
          },
        ]);
        setCurrentWorkspaceId(user.workspace_id);
      }
    } finally {
      setIsLoadingWorkspaces(false);
    }
  }, [isAuthenticated, isAuthLoading, user]);

  // Switch to a different workspace
  const switchWorkspace = useCallback(async (workspaceId: string) => {
    try {
      // Verify workspace is available
      const workspace = availableWorkspaces.find(
        (w) => w.workspace_id === workspaceId
      );
      if (!workspace) {
        throw new Error('Workspace not accessible');
      }

      // Update localStorage first so it persists across reload
      setStoredWorkspaceId(workspaceId);

      // Update API client immediately so any in-flight requests use the new workspace
      setWorkspaceIdGetter(() => workspaceId);

      // Update state
      setCurrentWorkspaceId(workspaceId);

      // Clear workspace-specific data
      setSessions([]);
      setCurrentSessionId(null);
      setChatHistory([]);
      setCurrentInvestigation(null);
      setInsights([]);
      setInvestigationState(InvestigationState.Idle);

      // Navigate to home - full reload ensures clean state with new workspace
      window.location.href = '/';
    } catch (err) {
      console.error('Failed to switch workspace:', err);
      // Keep current workspace, show error
      setError('Failed to switch workspace. Please try again.');
    }
  }, [availableWorkspaces]);

  // Fetch workspaces when authenticated
  useEffect(() => {
    if (isAuthenticated && !isAuthLoading) {
      fetchWorkspaces();
    }
  }, [isAuthenticated, isAuthLoading, fetchWorkspaces]);

  // Handle workspace access denied
  useEffect(() => {
    const handleWorkspaceAccessDenied = () => {
      if (user?.workspace_id) {
        console.error('Workspace access denied, resetting to primary workspace');
        clearStoredWorkspaceId();
        setCurrentWorkspaceId(user.workspace_id);
        setError('Access to workspace was revoked. Switched to your primary workspace.');
        // Navigate to home
        window.location.href = '/';
      }
    };

    window.addEventListener('workspace-access-denied', handleWorkspaceAccessDenied);
    return () => {
      window.removeEventListener('workspace-access-denied', handleWorkspaceAccessDenied);
    };
  }, [user?.workspace_id]);

  // Session Actions
  const startNewSession = useCallback(async (isTourDemo: boolean = false, dataSourceIds?: string[]): Promise<string> => {
    try {
      // Create investigation via API
      const response = await investigationApi.create({
        title: isTourDemo ? '[TOUR DEMO] New Investigation' : 'New Investigation',
        ...(dataSourceIds && dataSourceIds.length > 0
          ? { data_source_ids: dataSourceIds }
          : { data_source_id: null }),
        is_tour_demo: isTourDemo,
      });

      // Verify the investigation is readable before returning
      // This ensures the write is fully persisted and accessible
      // Retry up to 5 times with exponential backoff (50ms, 100ms, 200ms, 400ms, 800ms)
      let verified = false;
      for (let attempt = 0; attempt < 5 && !verified; attempt++) {
        try {
          await investigationApi.get(response.id);
          verified = true;
        } catch (verifyErr) {
          // Wait before retry with exponential backoff
          await new Promise(resolve => setTimeout(resolve, 50 * Math.pow(2, attempt)));
        }
      }

      if (!verified) {
        console.warn('Investigation created but verification timed out, proceeding anyway');
      }

      const newSession: InvestigationSession = {
        id: response.id,
        title: response.title,
        lastActive: new Date(response.created_at + 'Z'),
        lastActiveRaw: response.created_at,
        messages: [],
        dataSourceIds: response.data_source_ids || dataSourceIds || [],
      };
      setSessions((prev) => [newSession, ...prev]);
      setCurrentSessionId(response.id);
      setChatHistory([]);
      setCurrentInvestigation(null);
      setInvestigationState(InvestigationState.Input);
      return response.id;
    } catch (err) {
      console.error('Failed to create investigation via API:', err);
      // Re-throw API validation errors (4xx) so the caller can show them to the user
      if (err instanceof Error && 'status' in err && typeof (err as { status: number }).status === 'number') {
        const status = (err as { status: number }).status;
        if (status >= 400 && status < 500) {
          throw err;
        }
      }
      // Fallback to local ID only for network/server errors
      const fallbackId = `local-${Date.now()}`;
      const nowStr = new Date().toISOString();
      const newSession: InvestigationSession = {
        id: fallbackId,
        title: isTourDemo ? '[TOUR DEMO] New Investigation' : 'New Investigation',
        lastActive: new Date(),
        lastActiveRaw: nowStr,
        messages: [],
      };
      setSessions((prev) => [newSession, ...prev]);
      setCurrentSessionId(fallbackId);
      setChatHistory([]);
      setCurrentInvestigation(null);
      setInvestigationState(InvestigationState.Input);
      return fallbackId;
    }
  }, []);

  const loadSession = useCallback((id: string) => {
    const session = sessions.find((s) => s.id === id);
    if (session) {
      setCurrentSessionId(id);
      setChatHistory(session.messages);
      setInvestigationState(
        session.messages.length > 0 ? InvestigationState.Result : InvestigationState.Input
      );
    } else {
      // Session not in local state - still set the ID so API calls work
      // This handles navigating directly to a backend investigation
      setCurrentSessionId(id);
      setChatHistory([]);
      setCurrentInvestigation(null);
      setInvestigationState(InvestigationState.Input);
    }
  }, [sessions]);

  const deleteSession = useCallback(async (id: string): Promise<void> => {
    try {
      // Delete via API (this will cascade delete messages and pins on the backend)
      await investigationApi.delete(id);
    } catch (err) {
      console.error('Failed to delete investigation via API:', err);
      // Continue with local deletion even if API fails
    }

    // Remove from local state
    setSessions((prev) => prev.filter((s) => s.id !== id));

    if (currentSessionId === id) {
      setCurrentSessionId(null);
      setChatHistory([]);
      setCurrentInvestigation(null);
      setInvestigationState(InvestigationState.Idle);
    }
  }, [currentSessionId]);

  // Chat Actions
  const addChatMessage = useCallback((message: ChatMessage) => {
    setChatHistory((prev) => [...prev, message]);
  }, []);

  // Error Actions
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Derive self-service tier flags from current workspace
  const { isSelfService, isFreeTier } = deriveTierFlags(availableWorkspaces, currentWorkspaceId);

  // Derive demo data source state from current workspace membership
  const currentWsMembership = availableWorkspaces.find(w => w.workspace_id === currentWorkspaceId);
  const hasDemoDataSource = currentWsMembership?.has_demo_data_source ?? false;
  const demoDataSourceName = currentWsMembership?.demo_data_source_name ?? null;
  const [demoDismissed, setDemoDismissed] = useState(false);

  // Sync demoDismissed from workspace membership data
  useEffect(() => {
    if (currentWsMembership) {
      setDemoDismissed(currentWsMembership.demo_dismissed);
    }
  }, [currentWsMembership?.demo_dismissed]);

  const dismissDemo = useCallback(async () => {
    setDemoDismissed(true);
  }, []);

  const value: AppContextType = {
    mode,
    setMode,
    investigationState,
    setInvestigationState,
    query,
    setQuery,
    currentSessionId,
    sessions,
    startNewSession,
    loadSession,
    deleteSession,
    refreshSessions,
    isLoadingSessions,
    insights,
    setInsights,
    currentInvestigation,
    setCurrentInvestigation,
    chatHistory,
    setChatHistory,
    addChatMessage,
    isLoading,
    setIsLoading,
    error,
    setError,
    clearError,
    currentWorkspaceId,
    setCurrentWorkspaceId,
    availableWorkspaces,
    switchWorkspace,
    isLoadingWorkspaces,
    userProfileId,
    userProfileName,
    isSelfService,
    isFreeTier,
    tourCompleted,
    hasDemoDataSource,
    demoDataSourceName,
    demoDismissed,
    dismissDemo,
    featureFlags,
    isFeatureFlagsLoaded,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
